import { useCallback, useState } from "react";
import { BeneficiaryContribution } from "../types/contributions";

export const useBeneficiaryState = () => {
  const [beneficiaries, setBeneficiaries] = useState<BeneficiaryContribution[]>(
    []
  );
  const [fundConfig, setFundConfig] = useState<{
    personal: { total: number; label: string };
    employer: { total: number; label: string };
  }>({
    personal: { total: 0, label: "Personal contribution funds" },
    employer: { total: 0, label: "Employer contribution funds" },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoAllocationTimeouts, setAutoAllocationTimeouts] = useState<{
    personal: NodeJS.Timeout | null;
    employer: NodeJS.Timeout | null;
  }>({ personal: null, employer: null });

  const handleAutoAllocation = useCallback(
    (
      beneficiaries: BeneficiaryContribution[],
      fundTotal: number,
      type: "personal" | "employer"
    ) => {
      if (fundTotal <= 0) return beneficiaries;

      const totalAllocated = beneficiaries.reduce((sum, b) => {
        const amount =
          type === "personal"
            ? b.employeeContributionInDollars
            : b.employerContributionInDollars;
        return sum + amount;
      }, 0);

      const remainingAmount = fundTotal - totalAllocated;

      if (remainingAmount <= 0 || beneficiaries.length === 0) {
        return beneficiaries;
      }

      const unallocatedBeneficiaries = beneficiaries.filter((b) => {
        const amount =
          type === "personal"
            ? b.employeeContributionInDollars
            : b.employerContributionInDollars;
        return amount === 0;
      });

      let targetBeneficiary;

      if (unallocatedBeneficiaries.length === 1) {
        targetBeneficiary = unallocatedBeneficiaries[0];
      }
      // All allocated but shortage exists - allocate to largest => remember to ask ankur if to chnge this to lowest
      else if (unallocatedBeneficiaries.length === 0) {
        const largestBeneficiary = beneficiaries.reduce((largest, current) => {
          const currentAmount =
            type === "personal"
              ? current.employeeContributionInDollars
              : current.employerContributionInDollars;
          const largestAmount =
            type === "personal"
              ? largest.employeeContributionInDollars
              : largest.employerContributionInDollars;

          return currentAmount > largestAmount ? current : largest;
        });

        targetBeneficiary = largestBeneficiary;
      } else {
        return beneficiaries;
      }

      return beneficiaries.map((beneficiary) => {
        if (beneficiary.beneficiaryId !== targetBeneficiary.beneficiaryId) {
          return beneficiary;
        }

        if (type === "personal") {
          const newAmount =
            beneficiary.employeeContributionInDollars + remainingAmount;
          const newPercentage = (newAmount / fundTotal) * 100;
          return {
            ...beneficiary,
            employeeContributionInDollars: newAmount,
            employeeContributionInPercent: newPercentage,
          };
        } else {
          const newAmount =
            beneficiary.employerContributionInDollars + remainingAmount;
          const newPercentage = (newAmount / fundTotal) * 100;
          return {
            ...beneficiary,
            employerContributionInDollars: newAmount,
            employerContributionInPercent: newPercentage,
          };
        }
      });
    },
    []
  );

  const updatePersonalAllocation = useCallback(
    (beneficiaryId: number, newPercentage: number) => {
      setBeneficiaries((prev) => {
        const newAmount = (newPercentage / 100) * fundConfig.personal.total;

        return prev.map((beneficiary) => {
          if (beneficiary.beneficiaryId !== beneficiaryId) return beneficiary;

          return {
            ...beneficiary,
            employeeContributionInDollars: newAmount,
            employeeContributionInPercent: newPercentage,
          };
        });
      });

      if (autoAllocationTimeouts.personal) {
        clearTimeout(autoAllocationTimeouts.personal);
      }

      const timeoutId = setTimeout(() => {
        setBeneficiaries((prev) => {
          let rebalancedBeneficiaries = [...prev];

          const totalAllocated = rebalancedBeneficiaries.reduce(
            (sum, b) => sum + b.employeeContributionInDollars,
            0
          );

          if (totalAllocated > fundConfig.personal.total) {
            const excess = totalAllocated - fundConfig.personal.total;

            // Find beneficiary with largest allocation
            const otherBeneficiaries = rebalancedBeneficiaries.filter(
              (b) => b.beneficiaryId !== beneficiaryId
            );

            if (otherBeneficiaries.length > 0) {
              let largestBeneficiary = otherBeneficiaries[0];
              let largestAmount =
                largestBeneficiary.employeeContributionInDollars;

              otherBeneficiaries.forEach((b) => {
                if (b.employeeContributionInDollars > largestAmount) {
                  largestAmount = b.employeeContributionInDollars;
                  largestBeneficiary = b;
                }
              });

              // Reduce the largest allocation by the excess amount
              rebalancedBeneficiaries = rebalancedBeneficiaries.map(
                (beneficiary) => {
                  if (
                    beneficiary.beneficiaryId !==
                    largestBeneficiary.beneficiaryId
                  ) {
                    return beneficiary;
                  }

                  const newAmount = Math.max(
                    0,
                    beneficiary.employeeContributionInDollars - excess
                  );
                  const newPercentage =
                    (newAmount / fundConfig.personal.total) * 100;

                  return {
                    ...beneficiary,
                    employeeContributionInDollars: newAmount,
                    employeeContributionInPercent: newPercentage,
                  };
                }
              );
            }
          }

          return handleAutoAllocation(
            rebalancedBeneficiaries,
            fundConfig.personal.total,
            "personal"
          );
        });

        setAutoAllocationTimeouts((current) => ({
          ...current,
          personal: null,
        }));
      }, 1000);

      setAutoAllocationTimeouts((current) => ({
        ...current,
        personal: timeoutId,
      }));
    },
    [
      fundConfig.personal.total,
      handleAutoAllocation,
      autoAllocationTimeouts.personal,
    ]
  );

  const updateEmployerAllocation = useCallback(
    (beneficiaryId: number, newPercentage: number) => {
      setBeneficiaries((prev) => {
        const newAmount = (newPercentage / 100) * fundConfig.employer.total;

        return prev.map((beneficiary) => {
          if (beneficiary.beneficiaryId !== beneficiaryId) return beneficiary;

          return {
            ...beneficiary,
            employerContributionInDollars: newAmount,
            employerContributionInPercent: newPercentage,
          };
        });
      });

      if (autoAllocationTimeouts.employer) {
        clearTimeout(autoAllocationTimeouts.employer);
      }

      const timeoutId = setTimeout(() => {
        setBeneficiaries((prev) => {
          let rebalancedBeneficiaries = [...prev];

          const totalAllocated = rebalancedBeneficiaries.reduce(
            (sum, b) => sum + b.employerContributionInDollars,
            0
          );

          if (totalAllocated > fundConfig.employer.total) {
            const excess = totalAllocated - fundConfig.employer.total;

            const otherBeneficiaries = rebalancedBeneficiaries.filter(
              (b) => b.beneficiaryId !== beneficiaryId
            );

            if (otherBeneficiaries.length > 0) {
              let largestBeneficiary = otherBeneficiaries[0];
              let largestAmount =
                largestBeneficiary.employerContributionInDollars;

              otherBeneficiaries.forEach((b) => {
                if (b.employerContributionInDollars > largestAmount) {
                  largestAmount = b.employerContributionInDollars;
                  largestBeneficiary = b;
                }
              });

              rebalancedBeneficiaries = rebalancedBeneficiaries.map(
                (beneficiary) => {
                  if (
                    beneficiary.beneficiaryId !==
                    largestBeneficiary.beneficiaryId
                  ) {
                    return beneficiary;
                  }

                  const newAmount = Math.max(
                    0,
                    beneficiary.employerContributionInDollars - excess
                  );
                  const newPercentage =
                    (newAmount / fundConfig.employer.total) * 100;

                  return {
                    ...beneficiary,
                    employerContributionInDollars: newAmount,
                    employerContributionInPercent: newPercentage,
                  };
                }
              );
            }
          }

          return handleAutoAllocation(
            rebalancedBeneficiaries,
            fundConfig.employer.total,
            "employer"
          );
        });
        setAutoAllocationTimeouts((current) => ({
          ...current,
          employer: null,
        }));
      }, 1000);

      setAutoAllocationTimeouts((current) => ({
        ...current,
        employer: timeoutId,
      }));
    },
    [
      fundConfig.employer.total,
      handleAutoAllocation,
      autoAllocationTimeouts.employer,
    ]
  );

  const splitPersonalEqual = useCallback(() => {
    setBeneficiaries((prev) => {
      if (prev.length === 0) return prev;

      const equalPercentage = 100 / prev.length;
      const baseAmount =
        Math.floor((fundConfig.personal.total / prev.length) * 100) / 100;
      const totalBaseAmount = baseAmount * prev.length;
      const remainder = Number(
        (fundConfig.personal.total - totalBaseAmount).toFixed(2)
      );

      const centsToDistribute = Math.round(remainder * 100);

      return prev.map((beneficiary, index) => {
        const extraCents = index < centsToDistribute ? 0.01 : 0;
        const finalAmount = baseAmount + extraCents;

        return {
          ...beneficiary,
          employeeContributionInDollars: finalAmount,
          employeeContributionInPercent: equalPercentage,
        };
      });
    });
  }, [fundConfig.personal.total]);

  const splitEmployerEqual = useCallback(() => {
    setBeneficiaries((prev) => {
      if (prev.length === 0) return prev;

      const equalPercentage = 100 / prev.length;
      const baseAmount =
        Math.floor((fundConfig.employer.total / prev.length) * 100) / 100;
      const totalBaseAmount = baseAmount * prev.length;
      const remainder = Number(
        (fundConfig.employer.total - totalBaseAmount).toFixed(2)
      );

      const centsToDistribute = Math.round(remainder * 100);

      return prev.map((beneficiary, index) => {
        const extraCents = index < centsToDistribute ? 0.01 : 0;
        const finalAmount = baseAmount + extraCents;

        return {
          ...beneficiary,
          employerContributionInDollars: finalAmount,
          employerContributionInPercent: equalPercentage,
        };
      });
    });
  }, [fundConfig.employer.total]);

  const resetPersonal = useCallback(() => {
    if (autoAllocationTimeouts.personal) {
      clearTimeout(autoAllocationTimeouts.personal);
      setAutoAllocationTimeouts((current) => ({ ...current, personal: null }));
    }

    setBeneficiaries((prev) =>
      prev.map((beneficiary) => ({
        ...beneficiary,
        employeeContributionInDollars: 0,
        employeeContributionInPercent: 0,
      }))
    );
  }, [autoAllocationTimeouts.personal]);

  const resetEmployer = useCallback(() => {
    if (autoAllocationTimeouts.employer) {
      clearTimeout(autoAllocationTimeouts.employer);
      setAutoAllocationTimeouts((current) => ({ ...current, employer: null }));
    }

    setBeneficiaries((prev) =>
      prev.map((beneficiary) => ({
        ...beneficiary,
        employerContributionInDollars: 0,
        employerContributionInPercent: 0,
      }))
    );
  }, [autoAllocationTimeouts.employer]);

  const adjustForRoundingErrors = useCallback(
    (
      beneficiaries: BeneficiaryContribution[],
      totalFund: number,
      type: "personal" | "employer"
    ) => {
      if (beneficiaries.length === 0) return beneficiaries;

      const totalAllocated = beneficiaries.reduce(
        (sum, b) =>
          sum +
          (type === "personal"
            ? b.employeeContributionInDollars
            : b.employerContributionInDollars),
        0
      );

      const difference = Number((totalFund - totalAllocated).toFixed(2));

      // If small difference (≤ 10 cents), distribute it
      if (Math.abs(difference) <= 0.1 && Math.abs(difference) > 0) {
        const adjustedBeneficiaries = [...beneficiaries];
        const centsToDistribute = Math.round(Math.abs(difference) * 100);
        const isPositive = difference > 0;

        for (
          let i = 0;
          i < centsToDistribute && i < adjustedBeneficiaries.length;
          i++
        ) {
          const adjustment = isPositive ? 0.01 : -0.01;

          if (type === "personal") {
            adjustedBeneficiaries[i].employeeContributionInDollars = Number(
              (
                adjustedBeneficiaries[i].employeeContributionInDollars +
                adjustment
              ).toFixed(2)
            );
          } else {
            adjustedBeneficiaries[i].employerContributionInDollars = Number(
              (
                adjustedBeneficiaries[i].employerContributionInDollars +
                adjustment
              ).toFixed(2)
            );
          }
        }

        return adjustedBeneficiaries;
      }

      return beneficiaries;
    },
    []
  );

  return {
    beneficiaries,
    setBeneficiaries,
    fundConfig,
    setFundConfig,
    loading,
    setLoading,
    error,
    setError,
    updatePersonalAllocation,
    updateEmployerAllocation,
    splitPersonalEqual,
    splitEmployerEqual,
    resetPersonal,
    resetEmployer,
    adjustForRoundingErrors,
  };
};
